import { FC, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';

import { Table, TableColumn } from '@servicetitan/table';
import { useInvariableEffect } from '@servicetitan/react-hooks';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { Stack, TableNoRecords } from '@servicetitan/design-system';

import { EmptyState } from './empty-state';
import { StatusFilter } from './status-filter';
import { StatusCell } from './cells/status-cell';
import { PartnerFilter } from './partner-filter';
import { ActionsCell } from './cells/actions-cell';
import { TenantsStore } from '../../stores/tenants.store';
import { LoadStatus } from '../../../common/enums/load-status';
import { DebouncedSearch } from '../debounced-search/debounced-search';
import { tenantsQueryParamsHandler } from '../../utils/tenants-query-params-handler';

export const Tenants: FC = provide({ singletons: [TenantsStore] })(
    observer(() => {
        const [{ tenantsLoadStatus, queryParams, tableState, searchFilter, filterBySearch, init }] =
            useDependencies(TenantsStore);

        useInvariableEffect(() => {
            const initialQueryParams = tenantsQueryParamsHandler.parse(window.location.search);

            init(initialQueryParams);
        });

        const navigate = useNavigate();

        useEffect(() => {
            if (queryParams) {
                navigate(
                    { search: tenantsQueryParamsHandler.toString(queryParams) },
                    { replace: true },
                );
            }
        }, [queryParams, navigate]);

        return (
            <Stack direction="column" spacing={2}>
                <Stack spacing={2}>
                    <DebouncedSearch
                        onChange={filterBySearch}
                        externalValue={searchFilter}
                        placeholder="Search by Name or ID"
                    />
                    <Stack spacing={1}>
                        <StatusFilter />
                        <PartnerFilter />
                    </Stack>
                </Stack>
                <Table
                    sortable
                    striped={false}
                    tableState={tableState}
                    loading={tenantsLoadStatus === LoadStatus.Loading}
                >
                    <TableColumn field="displayName" title="Name" />
                    <TableColumn field="externalId" title="ID" />
                    <TableColumn field="partnerName" title="Partner" />
                    <TableColumn field="status" title="Status" cell={StatusCell} />
                    <TableColumn
                        locked
                        field=""
                        width={110}
                        cell={ActionsCell}
                        headerCell={() => null}
                    />
                    <TableNoRecords>
                        <EmptyState />
                    </TableNoRecords>
                </Table>
            </Stack>
        );
    }),
);
